<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'

import {MoreHorizontal, Pencil, Unlink} from 'lucide-vue-next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'

import type { ReportTemplate } from '@/types'
import CreateTemplateDialog from "@/pages/Project/Templates/CreateTemplateDialog.vue";

const store = useStore()

const props = defineProps<{
  project_id: number
  template: ReportTemplate
}>()

const confirmDialogRef = ref<InstanceType<typeof DialogConfirm> | null>(null)

const remove = async (ev: any) => {
  store.dispatch('geo/deleteReportTemplate', {
    project_id: props.project_id,
    template: props.template
  })
}

const onUnattachClick = () => {
  confirmDialogRef.value?.open()
}

const handleConfirmUnattach = async () => {
  await store.dispatch('geo/unattachTemplateFromProject', {
    projectId: props.project_id,
    template: props.template
  })
}
</script>

<template>
  <div class="flex items-center">
    <!-- Only show edit button if not a system template -->
    <CreateTemplateDialog
      v-if="!props.template.is_system_template"
      :project-id="props.project_id"
      :template="props.template"
      :edit-mode="true"
    >
      <Button variant="ghost" size="sm" data-testid="template-edit-button"><Pencil class="w-3.5 h-3.5" /></Button>
    </CreateTemplateDialog>

    <!-- Show Library badge for system templates -->
    <TooltipProvider v-else>
      <Tooltip>
        <TooltipTrigger as-child>
          <Badge
            variant="secondary"
            class="bg-blue-100 text-blue-700 hover:bg-blue-100 cursor-default"
            data-testid="template-library-badge"
          >
            Library
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>This is a library template managed by administrators</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>

    <!-- Dropdown for non-system templates -->
    <DropdownMenu v-if="!props.template.is_system_template">
      <DropdownMenuTrigger as-child>
        <Button variant="ghost" class="w-8 h-8 p-0" data-testid="template-dropdown-trigger">
          <span class="sr-only">Open menu</span>
          <MoreHorizontal class="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem @click="remove" class="text-red-600" data-testid="template-delete-action">Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Remove from project button for system templates -->
    <TooltipProvider v-else>
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
            variant="ghost"
            size="sm"
            class="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
            @click="onUnattachClick"
            data-testid="template-unattach-button"
          >
            <Unlink class="w-4 h-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Remove from project</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>

  <!-- Confirmation dialog for unattaching system template -->
  <DialogConfirm
    ref="confirmDialogRef"
    title="Remove Template from Project"
    :description="`Are you sure you want to remove '${props.template.name}' from this project? The template will still be available in the Templates Library.`"
    confirm-text="Remove"
    variant="error"
    @confirm="handleConfirmUnattach"
  />
</template>
